import React, { useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import PaymentChoice from '../payment/PaymentChoice';
import PaymentReconciliationNotice from '../payment/PaymentReconciliationNotice';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';

const client = generateClient({ authMode: 'userPool' });

interface Props {
  eventId: string;
  member: any;
  familyMembers: any[]; // sibling Member records (from membersByFamilyGroup)
  onClose: () => void;
  onSuccess: () => void;
}

interface Selectable {
  label: string;
  memberId?: string;
  isPrimary?: boolean;
  isFamily?: boolean;
}

const CheckInModal: React.FC<Props> = ({ eventId, member, familyMembers, onClose, onSuccess }) => {
  const [eventTitle, setEventTitle] = useState('');
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const [agreements, setAgreements] = useState({ rules: false, conduct: false, waiver: false });
  const [guardianName, setGuardianName] = useState(
    `${member?.guardianFirstName || ''} ${member?.guardianLastName || ''}`.trim()
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Names already written to CompetitionDayRegistration when a multi-person
  // check-in fails part-way. Without this the member is told the whole thing
  // failed and retries, producing duplicate registrations for the people who
  // actually did get through.
  const [partiallyCheckedIn, setPartiallyCheckedIn] = useState<string[] | null>(null);
  // Once registration(s) are created, this holds the created record(s) so
  // the online-payment step below can render — a single record for a
  // one-person check-in, or the full list for a consolidated multi-person
  // (family) payment (added 15 Aug 2026).
  const [singleRegistration, setSingleRegistration] = useState<any>(null);
  const [multiRegistrations, setMultiRegistrations] = useState<any[] | null>(null);
  // #118: people the server reported as already checked in AND paid
  // (ALREADY_REGISTERED_AND_PAID, since #84). They are skipped, never sent to
  // payment, and listed in a summary; the rest of the family carries on.
  const [alreadyPaidNames, setAlreadyPaidNames] = useState<string[]>([]);
  const [allAlreadyPaid, setAllAlreadyPaid] = useState(false);
  const [paymentDone, setPaymentDone] = useState(false);
  // #121: pay on the day is not "Payment complete"; say where to pay.
  const [payOnDay, setPayOnDay] = useState(false);
  const [paymentReconciliationRef, setPaymentReconciliationRef] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  /**
   * Cancel from the payment step must genuinely cancel — the
   * registration record(s) created by handleSubmit are deleted via the
   * cancelCompetitionDayRegistration mutation (the frontend can't delete
   * them directly; the model grants delete to AdminsVerified only). Added
   * 15 Aug 2026 after a real report that pressing Cancel still left a
   * pending registration behind.
   */
  const handleCancelRegistrations = async () => {
    const created = multiRegistrations ?? (singleRegistration ? [singleRegistration] : []);
    const ids = created.map((r: any) => r?.id).filter(Boolean);
    if (ids.length === 0) {
      onClose();
      return;
    }
    setCancelling(true);
    setError(null);
    try {
      const res: any = await (client.mutations as any).cancelCompetitionDayRegistration({
        registrationIds: ids,
      });
      // Same double-JSON-encoding bug as handleSubmit below — fixed
      // alongside it, 15 Aug 2026. See parseLambdaResponse's doc comment.
      const data = parseLambdaResponse<{ success?: boolean; message?: string }>(res?.data);
      if (!data?.success) {
        setError(
          data?.message ||
            'We could not cancel that registration. Please contact the club so it can be sorted out.'
        );
        return;
      }
      onClose();
    } catch (e: any) {
      setError(
        e?.message ||
          'We could not cancel that registration. Please contact the club so it can be sorted out.'
      );
    } finally {
      setCancelling(false);
    }
  };

  const isJunior = member?.isJunior === true;
  const fee = 10;

  // Build the list of potential participants: me + family siblings
  const people: Selectable[] = [
    { label: `${member.firstName} ${member.lastName} (you)`, memberId: member.id, isPrimary: true },
    ...familyMembers.map((fm: any) => ({
      label: `${fm.firstName} ${fm.lastName}`,
      memberId: fm.id,
      isFamily: true,
    })),
  ];

  /**
   * #157 (G1): the phone to send for a given participant. Household rows
   * created by manage-family can have a blank phoneNumber (there is no
   * requirement that every family member supplies their own), which used to
   * reach the server as `phoneNumber || ''` and fail there even though the
   * household plainly has a usable number elsewhere. Falls back, in order,
   * to: the participant's own phone, the logged-in member's phone (covers a
   * family member checking in with no phone of their own, logged in as the
   * primary), then the household primary's phone (covers the primary
   * themself having no phone, but a family member does).
   */
  const resolveParticipantPhone = (memberId: string): string => {
    const self = memberId === member.id ? member : familyMembers.find((fm: any) => fm.id === memberId);
    if (self?.phoneNumber) return self.phoneNumber;
    if (member?.phoneNumber) return member.phoneNumber;
    const primary = member?.familyPrimary ? member : familyMembers.find((fm: any) => fm?.familyPrimary);
    return primary?.phoneNumber || '';
  };

  useEffect(() => {
    (async () => {
      try {
        const { data: evt } = await (client.models as any).Event.get({ id: eventId });
        if (evt?.title) setEventTitle(evt.title);
      } catch {
        /* ignore */
      }
    })();
    // Preselect the logged-in member
    setSelection({ [member.id]: true });
  }, [eventId, member.id]);

  const selectedIds = Object.entries(selection)
    .filter(([, v]) => v)
    .map(([k]) => k);
  const total = selectedIds.length * fee;

  const handleSubmit = async () => {
    setError(null);
    if (selectedIds.length === 0) {
      setError('Select at least one person to check in.');
      return;
    }
    if (!agreements.rules || !agreements.conduct || !agreements.waiver) {
      setError(
        isJunior
          ? 'Guardian must accept all three on behalf of the family.'
          : 'Please accept all three agreements.'
      );
      return;
    }
    if (isJunior && !guardianName.trim()) {
      setError('Guardian name is required.');
      return;
    }

    setSubmitting(true);
    // Declared outside the try so the catch can report who was already written
    // before the failure — see the partial-check-in handling below.
    const createdRegistrations: any[] = [];
    const skippedPaid: string[] = [];
    try {
      // Every selected person gets their own CompetitionDayRegistration
      // record via submitCompetitionDayRegistration (unchanged). Online
      // payment is then offered as ONE consolidated Square charge covering
      // all of them (added 15 Aug 2026 — see
      // amplify/functions/process-payment/rules.ts's
      // validateMultiCompRegistrationTargets) rather than pay-on-the-day
      // being the only option for a multi-person check-in.
      for (const memberId of selectedIds) {
        const person = people.find((p) => p.memberId === memberId);
        const isSelf = person?.isPrimary;
        const name = isSelf ? `${member.firstName} ${member.lastName}` : person?.label || '';
        const res: any = await (client.mutations as any).submitCompetitionDayRegistration({
          eventId,
          participantName: name,
          participantEmail: member.email,
          participantPhone: resolveParticipantPhone(memberId),
          registrationType: isSelf ? 'INDIVIDUAL_MEMBER' : 'FAMILY_CHECKIN',
          memberId,
          familyMemberName: isSelf ? undefined : name,
          guardianConsentNote: isJunior
            ? `Guardian consent: ${guardianName.trim()} at ${new Date().toISOString()}`
            : undefined,
        });
        // GraphQL-level errors are reported on res.errors, NOT thrown, and were
        // previously ignored here entirely — so a transport/authorization/
        // serialisation failure fell straight through to the `!data?.success`
        // branch and surfaced as the generic fallback with the real cause
        // discarded. On 15 Aug 2026 a family check-in wrote its
        // CompetitionDayRegistration successfully (Lambda returned
        // success: true, record persisted, no error logged) while the member saw
        // only "Check-in failed. Please try again." and could not reach payment.
        // With AppSync request logging disabled the true error was unrecoverable
        // server-side, so it has to be surfaced here.
        if (res?.errors?.length) {
          const detail = res.errors
            .map((x: any) => x?.message)
            .filter(Boolean)
            .join('; ');
          console.error('CHECKIN_GRAPHQL_ERRORS', res.errors);
          throw new Error(detail || 'Check-in failed. Please try again.');
        }
        // BUG FIXED 15 Aug 2026 (real production incident, confirmed via
        // browser console on a second occurrence): this previously did only
        // a SINGLE JSON.parse pass (`typeof res.data === 'string' ?
        // JSON.parse(res.data) : res.data`). AppSync's custom-mutation
        // response for a Lambda that itself returns JSON.stringify(...) is
        // encoded through TWO layers of string-wrapping (see
        // parseLambdaResponse's own doc comment) — every other Lambda call
        // site in this codebase (CompetitionsList.tsx, MemberDashboard.tsx,
        // etc.) already uses parseLambdaResponse for exactly this reason.
        // After one parse here, `data` was still a JSON *string*, so
        // `data?.success` was always undefined regardless of the real
        // response — meaning EVERY check-in reported failure 100% of the
        // time even though the Lambda ran clean and the record was written.
        // This is not the transient/mobile-signal issue guessed at in
        // BACKLOG M0h; it is a deterministic bug on every single call.
        const data = parseLambdaResponse<{
          success?: boolean;
          message?: string;
          error?: string;
          alreadyPaid?: boolean;
          registration?: any;
        }>(res?.data);
        // #118: already checked in and paid is "done" for this person, not a
        // failure. Skip them (no second row, no payment) and carry on.
        if (!data?.success && (data?.error === 'ALREADY_REGISTERED_AND_PAID' || data?.alreadyPaid === true)) {
          skippedPaid.push(data?.registration?.participantName || name);
          continue;
        }
        if (!data?.success) {
          console.error('CHECKIN_UNSUCCESSFUL_RESPONSE', { res, data });
          throw new Error(data?.message || 'Check-in failed. Please try again.');
        }
        if (!data.registration?.id) {
          // Guard: a success response with no usable registration id would
          // otherwise reach PaymentChoice with entityId undefined and fail
          // there instead, after the record had already been written.
          console.error('CHECKIN_MISSING_REGISTRATION_ID', { data });
          throw new Error(
            'Check-in was recorded but the confirmation was incomplete. Please check with an organiser before paying.'
          );
        }
        createdRegistrations.push(data.registration);
      }

      setAlreadyPaidNames(skippedPaid);
      if (createdRegistrations.length === 0 && skippedPaid.length > 0) {
        setAllAlreadyPaid(true);
      } else if (createdRegistrations.length === 1) {
        setSingleRegistration(createdRegistrations[0]);
      } else {
        setMultiRegistrations(createdRegistrations);
      }
    } catch (e: any) {
      // The Amplify data client rejects with a plain `{ errors: [...] }` object,
      // not an Error — so `e.message` is undefined and every distinct failure
      // collapsed into the same meaningless fallback string. Read errors[] first.
      const fromErrors = Array.isArray(e?.errors)
        ? e.errors
            .map((x: any) => x?.message)
            .filter(Boolean)
            .join('; ')
        : null;
      console.error('CHECKIN_FAILED', e);
      setError(fromErrors || e?.message || 'Check-in failed. Please try again.');
      // If some people in a multi-person check-in were already written before
      // the failure, say so rather than implying nothing happened — retrying
      // blind is what produces duplicate registrations.
      if (createdRegistrations.length > 0) {
        setPartiallyCheckedIn(createdRegistrations.map((r: any) => r?.participantName).filter(Boolean));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {allAlreadyPaid ? (
            <>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Already checked in</h2>
              <p role="status" className="text-sm text-gray-700 mb-4">
                <strong>Already checked in and paid:</strong> {alreadyPaidNames.join(', ')}. No further payment is
                needed. Good luck today!
              </p>
              <div className="flex justify-end">
                <button
                  onClick={onSuccess}
                  className="px-4 py-2 bg-fishing-blue text-white rounded-md text-sm font-semibold hover:bg-blue-700"
                >
                  Done
                </button>
              </div>
            </>
          ) : multiRegistrations ? (
            <>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Check-In Successful!</h2>
              <p className="text-sm text-gray-600 mb-4">
                {multiRegistrations.length} people have been checked in for the competition.
              </p>
              {alreadyPaidNames.length > 0 && (
                <div role="status" className="bg-green-50 border border-green-200 rounded-md p-2 text-sm text-green-800 mb-4">
                  <strong>Already checked in and paid:</strong> {alreadyPaidNames.join(', ')}. No payment needed for{' '}
                  {alreadyPaidNames.length === 1 ? 'them' : 'those people'}.
                </div>
              )}

              {paymentReconciliationRef && (
                <PaymentReconciliationNotice paymentReference={paymentReconciliationRef} />
              )}

              <p className="text-sm text-gray-600 mb-4">
                {paymentReconciliationRef
                  ? ''
                  : paymentDone
                    ? payOnDay
                      ? `You're all set. Please pay $${fee * multiRegistrations.length} at the sign-on desk. Good luck today!`
                      : "Payment complete. You're all set — good luck today!"
                    : `Choose how you'd like to pay the combined entry fee for all ${multiRegistrations.length}:`}
              </p>

              {!paymentDone && !paymentReconciliationRef && multiRegistrations.every((r) => r?.id) && (
                <div className="mb-4">
                  <PaymentChoice
                    amountCents={fee * 100 * multiRegistrations.length}
                    amountDisplay={`$${(fee * multiRegistrations.length).toFixed(2)}`}
                    paymentType="COMPETITION_ENTRY"
                    entityId={multiRegistrations[0].id}
                    entityIds={multiRegistrations.map((r) => r.id)}
                    entityType="COMPETITION_DAY_REGISTRATION"
                    payerEmail={member.email}
                    payerName={`${member.firstName} ${member.lastName}`}
                    feeKey="competition_fee_member"
                    authMode="userPool"
                    onSuccess={() => setPaymentDone(true)}
                    onReconciliationRequired={(paymentId) => setPaymentReconciliationRef(paymentId)}
                    onPayOnDay={() => { setPayOnDay(true); setPaymentDone(true); }}
                  />
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-2 text-sm text-red-700 mb-3">
                  {error}
                </div>
              )}

              <div className="flex gap-3 justify-end">
                {!paymentDone && !paymentReconciliationRef && (
                  <button
                    onClick={handleCancelRegistrations}
                    disabled={cancelling}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {cancelling ? 'Cancelling…' : 'Cancel check-in'}
                  </button>
                )}
                <button
                  onClick={onSuccess}
                  className="px-4 py-2 bg-fishing-blue text-white rounded-md text-sm font-semibold hover:bg-blue-700"
                >
                  Done
                </button>
              </div>
            </>
          ) : singleRegistration ? (
            <>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Check-In Successful!</h2>
              <p className="text-sm text-gray-600 mb-4">
                {singleRegistration.participantName} has been checked in for the competition.
              </p>
              {alreadyPaidNames.length > 0 && (
                <div role="status" className="bg-green-50 border border-green-200 rounded-md p-2 text-sm text-green-800 mb-4">
                  <strong>Already checked in and paid:</strong> {alreadyPaidNames.join(', ')}. No payment needed for{' '}
                  {alreadyPaidNames.length === 1 ? 'them' : 'those people'}.
                </div>
              )}

              {paymentReconciliationRef && (
                <PaymentReconciliationNotice paymentReference={paymentReconciliationRef} />
              )}

              <p className="text-sm text-gray-600 mb-4">
                {paymentReconciliationRef
                  ? ''
                  : paymentDone
                    ? payOnDay
                      ? `You're all set. Please pay $${singleRegistration.feeAmount ?? fee} at the sign-on desk. Good luck today!`
                      : "Payment complete. You're all set — good luck today!"
                    : "Choose how you'd like to pay your entry fee:"}
              </p>

              {!paymentDone && !paymentReconciliationRef && singleRegistration.id && (
                <div className="mb-4">
                  <PaymentChoice
                    amountCents={1000}
                    amountDisplay="$10.00"
                    paymentType="COMPETITION_ENTRY"
                    entityId={singleRegistration.id}
                    entityType="COMPETITION_DAY_REGISTRATION"
                    payerEmail={singleRegistration.participantEmail ?? member.email}
                    payerName={singleRegistration.participantName ?? ''}
                    feeKey="competition_fee_member"
                    authMode="userPool"
                    onSuccess={() => setPaymentDone(true)}
                    onReconciliationRequired={(paymentId) => setPaymentReconciliationRef(paymentId)}
                    onPayOnDay={() => { setPayOnDay(true); setPaymentDone(true); }}
                  />
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-2 text-sm text-red-700 mb-3">
                  {error}
                </div>
              )}

              <div className="flex gap-3 justify-end">
                {!paymentDone && !paymentReconciliationRef && (
                  <button
                    onClick={handleCancelRegistrations}
                    disabled={cancelling}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {cancelling ? 'Cancelling…' : 'Cancel check-in'}
                  </button>
                )}
                <button
                  onClick={onSuccess}
                  className="px-4 py-2 bg-fishing-blue text-white rounded-md text-sm font-semibold hover:bg-blue-700"
                >
                  Done
                </button>
              </div>
            </>
          ) : (
            <>
          <h2 className="text-xl font-bold text-gray-900 mb-1">
            Check in to {eventTitle || 'competition'}
          </h2>
          <p className="text-sm text-gray-500 mb-4">$10 per person. Pay online now or on the day.</p>

          <div className="space-y-2 mb-4">
            {people.map((p) => (
              <label
                key={p.memberId}
                className="flex items-center gap-3 p-3 border rounded-md hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={!!selection[p.memberId!]}
                  onChange={(e) =>
                    setSelection((prev) => ({ ...prev, [p.memberId!]: e.target.checked }))
                  }
                  className="h-4 w-4 text-fishing-blue focus:ring-fishing-blue rounded"
                />
                <span className="text-sm text-gray-900">{p.label}</span>
              </label>
            ))}
          </div>

          <div className="bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-700 mb-4">
            Total: <strong>{selectedIds.length}</strong> × ${fee} ={' '}
            <strong className="text-fishing-blue">${total}</strong>
          </div>

          {isJunior && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
              <p className="text-sm font-semibold text-amber-900 mb-1">Guardian consent required</p>
              <p className="text-xs text-amber-800 mb-2">
                A parent or guardian must accept the club rules, code of conduct, and waiver on
                behalf of this junior.
              </p>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Guardian name *
              </label>
              <input
                type="text"
                value={guardianName}
                onChange={(e) => setGuardianName(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
              />
            </div>
          )}

          <div className="space-y-2 mb-4 text-sm">
            {(
              [
                ['rules', 'I accept the Club Rules'],
                ['conduct', 'I accept the Code of Conduct'],
                ['waiver', 'I accept the Waiver and Release'],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreements[key]}
                  onChange={(e) => setAgreements((prev) => ({ ...prev, [key]: e.target.checked }))}
                  className="mt-1 h-4 w-4 text-fishing-blue rounded"
                />
                <span className="text-gray-700">
                  {isJunior ? `${label} (guardian consent)` : label}
                </span>
              </label>
            ))}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-2 text-sm text-red-700 mb-3">
              {error}
            </div>
          )}

          {partiallyCheckedIn && partiallyCheckedIn.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-2 text-sm text-amber-800 mb-3">
              <strong>Already checked in:</strong> {partiallyCheckedIn.join(', ')}. Don&apos;t
              re-submit for {partiallyCheckedIn.length === 1 ? 'them' : 'those people'} — check with
              an organiser instead, or you&apos;ll end up registered twice.
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-4 py-2 bg-fishing-blue text-white rounded-md text-sm font-semibold hover:bg-blue-700 disabled:bg-gray-300"
            >
              {submitting ? 'Checking in…' : 'Complete check-in'}
            </button>
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckInModal;
